import { GALLEY_WALKWAY_PADDING } from '../../../constants';
import { ToolDefinition } from '../types';

export const getToolDefinitionsCfn = (): ToolDefinition[] => {
	return [
		// Material tools
		{
			name: 'set_countertop_material',
			description:
				'[CLIENT] Updates the countertop material. REQUIRES valid materialKey from get_countertop_material_options.\n\n' +
				'WORKFLOW:\n' +
				'1. Call get_countertop_material_options first\n' +
				'2. Match user request to returned options\n' +
				'3. If ONE match: Auto-apply with matched materialKey\n' +
				'4. If MULTIPLE: Show selection cards, wait for user click\n\n' +
				'NEVER fabricate material keys.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					materialKey: { type: 'string', description: 'The material key to set. MUST be a valid key from get_countertop_material_options.' }
				},
				required: ['materialKey']
			}
		},
		{
			name: 'set_surround_material',
			description:
				'[CLIENT] Updates the surround material. REQUIRES valid materialKey from get_surround_material_options.\n\n' +
				'WORKFLOW:\n' +
				'1. Call get_surround_material_options first\n' +
				'2. Match user request to returned options\n' +
				'3. If ONE match: Auto-apply with matched materialKey\n' +
				'4. If MULTIPLE: Show selection cards, wait for user click\n\n' +
				'NEVER fabricate material keys.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					materialKey: { type: 'string', description: 'The material key to set. MUST be a valid key from get_surround_material_options.' }
				},
				required: ['materialKey']
			}
		},

		// Island tools
		{
			name: 'add_island',
			description:
				'[CLIENT] Adds a new island to the kitchen: onto either end of the front row, or into a second row facing it.\n\n' +
				'USAGE:\n' +
				'- side="right" attaches the new island to the right of the rightmost front-row island\n' +
				'- side="left" attaches the new island to the left of the leftmost front-row island\n' +
				`- side="parallel" puts the new island in a SECOND ROW facing the kitchen, across a ${GALLEY_WALKWAY_PADDING}-inch walkway (the same walkway a galley uses)\n\n` +
				'ANGLE: Straight islands (grill, bar, smoker, pizza, asado) inherit the anchor island\'s axis angle. ' +
				'Corner islands are rotated 45deg relative to the anchor (anchorAngle+45 for right, anchorAngle-45 for left), ' +
				'so attaching a corner to a forward-facing (axis 0) anchor produces a 45deg corner on the right or a 315deg corner on the left. ' +
				'A parallel island is turned around to face the anchor (anchorAngle+180).\n\n' +
				'PARALLEL RULES:\n' +
				'- It anchors on the kitchen\'s default island, which must be forward-facing (axis 0); the tool refuses an angled anchor\n' +
				'- Only ONE island may face the kitchen. The tool refuses a second one and names the island that already faces the anchor\n' +
				'- The walkway has to be clear. An L- or U-shape side run reaches forward past the anchor and sits in it, so the tool refuses those shapes and names the island in the way\n' +
				'- A corner island can never be parallel\n' +
				'- Nothing can be attached beyond a parallel island: later side="left"/"right" calls go back to the front row\n' +
				'- It does not change the kitchen shape; it is not a substitute for set_kitchen_shape\n\n' +
				'The new island uses default dimensions for its type. Use resize_island afterwards to customize dimensions.\n\n' +
				'Examples:\n' +
				'- "Add a bar to the right" -> islandType="bar", side="right"\n' +
				'- "Add a smoker on the left end" -> islandType="smoker", side="left"\n' +
				'- "Add a corner on the right" -> islandType="corner", side="right" (placed at 45deg from a 0deg anchor)\n' +
				'- "Put a bar across from the grill" / "add an island parallel to this one" -> islandType="bar", side="parallel"',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					islandType: {
						type: 'string',
						enum: ['grill', 'bar', 'corner', 'smoker', 'pizza', 'asado'],
						description: 'The type of island to add.'
					},
					side: {
						type: 'string',
						enum: ['left', 'right', 'parallel'],
						description: 'Where the island goes: "left" attaches to the left of the leftmost island, "right" attaches to the right of the rightmost island, ' +
							`"parallel" places it in a second row facing the kitchen across a ${GALLEY_WALKWAY_PADDING}-inch walkway.`
					}
				},
				required: ['islandType', 'side']
			}
		},
		{
			name: 'resize_island',
			description:
				'[CLIENT] Resizes an island to new dimensions (in inches).\n\n' +
				'All dimension parameters are optional - omitted dimensions retain their current values.\n' +
				'Minimum width is enforced based on appliances: if the requested width is too small, ' +
				'the island will be resized to the minimum possible width and the response will indicate the constraint.\n\n' +
				'Examples:\n' +
				'- "Make the island 8 feet wide" -> pass only width (96 inches)\n' +
				'- "Resize island 1 to 10ft x 3ft x 36in" -> pass all dimensions',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					islandId: { type: 'string', description: 'The ID of the island to resize. Use list_islands to get valid IDs.' },
					width: { type: 'number', description: 'New width in inches. Optional - uses current width if not provided.' },
					height: { type: 'number', description: 'New height in inches. Optional - uses current height if not provided.' },
					depth: { type: 'number', description: 'New depth in inches. Optional - uses current depth if not provided.' },
					overrideMinimum: { type: 'boolean', description: 'When true, ignores the island minimum size constraint and resets the minimum to the new size. Defaults to false.' }
				},
				required: ['islandId']
			}
		},
		{
			name: 'update_island_minimum_size',
			description:
				'[CLIENT] Updates the minimum size metadata for an island. The minimum size prevents resize_island from shrinking ' +
				'the island below these dimensions (unless overrideMinimum is used).\n\n' +
				'All dimension parameters are optional - omitted dimensions retain their current minimum values.\n\n' +
				'The minimum size defaults to the island\'s size when it was created/loaded.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					islandId: { type: 'string', description: 'The ID of the island to update. Use list_islands to get valid IDs.' },
					width: { type: 'number', description: 'New minimum width in inches. Optional - uses current minimum width if not provided.' },
					height: { type: 'number', description: 'New minimum height in inches. Optional - uses current minimum height if not provided.' },
					depth: { type: 'number', description: 'New minimum depth in inches. Optional - uses current minimum depth if not provided.' }
				},
				required: ['islandId']
			}
		},
		{
			name: 'remove_island',
			description:
				'[CLIENT] Removes an island from the kitchen design.\n\n' +
				'WORKFLOW:\n' +
				'1. Call list_appliances to get islands (async - wait for TOOL_RESULT)\n' +
				'2. Match by island ID or shape\n' +
				'3. Cannot remove the only island\n\n' +
				'CONSTRAINTS:\n' +
				'- Cannot remove only island\n' +
				'- Cannot remove islands with multiple connections',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					islandId: { type: 'string', description: 'The ID of the island to remove. Use list_islands to get valid IDs.' }
				},
				required: ['islandId']
			}
		},
		{
			name: 'set_kitchen_shape',
			description:
				'[CLIENT] Changes the kitchen layout/shape by replacing all islands in the current draft.\n\n' +
				'WORKFLOW:\n' +
				'1. Call get_layout_options to see available shapes\n' +
				'2. Match user request to a shape option key\n' +
				'3. Apply with matched shapeKey\n\n' +
				'WARNING: Overwrites the current kitchen — all existing islands and appliances are removed.\n' +
				'The new kitchen starts empty (no appliances). Add appliances after setting the shape.\n' +
				'If appliances exist on the current islands and confirmed is not set, the tool will return an error requiring confirmation.\n' +
				'NEVER fabricate shape keys.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					shapeKey: { type: 'string', description: 'The shape key to apply. MUST be a valid key from get_layout_options.' },
					confirmed: { type: 'boolean', description: 'Set to true to confirm the shape change when appliances exist on the current islands. Required when appliances are present.' }
				},
				required: ['shapeKey']
			}
		},
		{
			name: 'list_islands',
			description:
				'[CLIENT ASYNC] Lists all islands in the kitchen design.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: list_islands] in history.\n\n' +
				'Use to discover island IDs before adding appliances, resizing, or removing islands.\n' +
				'Returns array of {id, width, height, depth, connectionCount}.',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},

		{
			name: 'clean_kitchen',
			description:
				'[CLIENT] Distributes all appliances evenly across the kitchen.\n\n' +
				'Iterates through every island, distributing appliance groups horizontally on each panel ' +
				'and centering items vertically within each group.\n\n' +
				'OPTIONAL: Pass autoResize: true to let each island resize itself to fit its appliances ' +
				'before distributing. An island whose run already holds its contents keeps the width it has — ' +
				'auto-resize only widens a run that has genuinely run out of room, and only ever gives back ' +
				'width auto-resize itself added.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					autoResize: {
						type: 'boolean',
						description: 'When true, automatically resize each island before distributing. Defaults to false.'
					},
					islandId: { type: 'string', description: 'Optional. When provided, only clean this specific island. Omit to clean all islands. Use list_islands to get valid IDs.' }
				}
			}
		},

		// Appliance tools
		{
			name: 'add_appliance',
			description:
				'[CLIENT] Adds an appliance to an island.\n\n' +
				'Use retrieve_appliance_candidates to find available SKUs first.\n\n' +
				'POSITIONING:\n' +
				'- Default (no placement): Creates a new group on the right side of the island\n' +
				'- placement left/right (no applianceId): Positions new group at the left/right edge\n' +
				'- placement left/right (with applianceId): Positions new group left/right of the reference appliance\'s group\n' +
				'- placement above/below (with applianceId): Adds to the SAME group as the reference, stacking vertically\n\n' +
				'WIDTH VALIDATION: If the island is not wide enough to accommodate the appliance, ' +
				'the tool will return an error indicating how many inches wider the island needs to be. ' +
				'Use resize_island to increase the width before retrying.\n\n' +
				'Examples:\n' +
				'- "Add a 36-inch grill" -> retrieve_appliance_candidates to find SKU, then add_appliance\n' +
				'- "Add a burner to the left of the grill" -> add_appliance with applianceId=grill-id, placement=left\n' +
				'- "Stack a warming drawer below the grill" -> add_appliance with applianceId=grill-id, placement=below',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					sku: { type: 'string', description: 'SKU of the appliance to add. Use get_appliances with a valid category to get available SKUs.' },
					islandId: { type: 'string', description: 'The UUID of the target island. Omit for single-island designs (auto-resolved). For multi-island designs, use list_islands to get valid UUIDs.' },
					applianceId: { type: 'string', description: 'Optional. The ID of an existing appliance to position relative to. Used with placement for precise positioning. Use list_appliances to get valid IDs.' },
					placement: {
						type: 'string',
						enum: ['left', 'right', 'above', 'below'],
						description: 'Optional. Where to place the new appliance. \'left\'/\'right\' creates a new group at that position. \'above\'/\'below\' (requires applianceId) adds to the same group as the reference appliance.'
					},
					autoSize: { type: 'boolean', description: 'Optional. When true, automatically resize the island to fit appliances after adding. Defaults to false.' }
				},
				required: ['sku']
			}
		},
		{
			name: 'add_cutout',
			description:
				'[CLIENT] Adds a cutout to an island\'s countertop.\n\n' +
				'Use list_islands to get valid island IDs, and get_appliances with category "Cutouts" for available cutout SKUs.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					sku: { type: 'string', description: 'SKU of the cutout to add. Use get_appliances with category "Cutouts" to get available SKUs.' },
					islandId: { type: 'string', description: 'The ID of the island to add the cutout to. Use list_islands to get valid IDs.' }
				},
				required: ['sku', 'islandId']
			}
		},
		{
			name: 'remove_appliance',
			description:
				'[CLIENT] Removes an appliance from the kitchen.\n\n' +
				'WORKFLOW:\n' +
				'1. Call list_appliances to find matching items (async - wait for TOOL_RESULT)\n' +
				'2. If ZERO matches: Tell user item not found\n' +
				'3. If ONE match: Remove using that ID\n' +
				'4. If MULTIPLE: Ask user to clarify',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					itemId: { type: 'string', description: 'The ID of the appliance to remove. Use list_appliances to get valid IDs.' },
					autoSize: { type: 'boolean', description: 'Optional. When true, automatically resize the island to fit remaining appliances after removal. Defaults to false.' }
				},
				required: ['itemId']
			}
		},
		{
			name: 'replace_appliance',
			description:
				'[CLIENT] Replaces an existing appliance with a different one. The appliance stays in the same group and position.\n\n' +
				'WORKFLOW:\n' +
				'1. Call list_appliances to find item ID\n' +
				'2. Use retrieve_appliance_candidates to find new SKU\n' +
				'3. Call this tool with applianceId and new SKU',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					applianceId: { type: 'string', description: 'The ID of the existing appliance to replace. Use list_appliances to get valid IDs.' },
					newSku: { type: 'string', description: 'SKU of the new appliance to swap in. Use get_appliances with a valid category to get available SKUs.' },
					autoSize: { type: 'boolean', description: 'Optional. When true, automatically resize the island to fit appliances after replacement. Defaults to false.' }
				},
				required: ['applianceId', 'newSku']
			}
		},
		{
			name: 'move_appliance_group',
			description:
				'[CLIENT] Moves an appliance group to a new position on the panel.\n\n' +
				'Repositions an appliance group relative to another appliance group or to an island edge, ' +
				'then distributes all groups horizontally for proper spacing.\n\n' +
				'USAGE:\n' +
				'- Relative to another appliance: pass referenceApplianceId and position (left/right)\n' +
				'  Example: "move the fridge left of the grill" -> applianceId=fridge, referenceApplianceId=grill, position=left\n' +
				'- Relative to island edge: pass only position (left/right), no referenceApplianceId\n' +
				'  Example: "move the fridge to the left side" -> applianceId=fridge, position=left\n' +
				'  Example: "move the fridge to the end of the island" -> applianceId=fridge, position=right',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					applianceId: { type: 'string', description: 'The ID of the appliance to move. The tool finds its appliance group automatically. Use list_appliances to get valid IDs.' },
					referenceApplianceId: { type: 'string', description: 'The ID of the reference appliance to position relative to. The tool finds its appliance group automatically. Omit to position relative to the island edge.' },
					position: { type: 'string', enum: ['left', 'right'], description: 'Where to place the group: \'left\' or \'right\' of the reference group or island edge.' }
				},
				required: ['applianceId', 'position']
			}
		},
		{
			name: 'move_appliance_to_island',
			description:
				'[CLIENT] Moves an appliance (and everything stacked in its column) to ANOTHER island, keeping its id.\n\n' +
				'Lands left/right of a reference appliance on the target island, or at an end of it. ' +
				'Companions the rules engine owns (a fridge trim kit, a jacket, vents) are regenerated at the destination. ' +
				'The target island grows when it has no room, unless autoSize is false or the island is a fixed size.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					applianceId: { type: 'string', description: 'The ID of the appliance to move. Use list_appliances to get valid IDs.' },
					islandId: { type: 'string', description: 'The ID of the island to move it to. Use list_islands to get valid IDs.' },
					referenceApplianceId: { type: 'string', description: 'Optional appliance on the target island to land next to.' },
					position: { type: 'string', enum: ['left', 'right'], description: "'left' or 'right' of the reference appliance, or of the island when no reference is given." },
					autoSize: { type: 'boolean', description: 'Allow the target island to grow to make room (default true).' }
				},
				required: ['applianceId', 'islandId']
			}
		},
		{
			name: 'swap_appliance_positions',
			description:
				'[CLIENT] Two appliances TRADE PLACES (their whole columns), on one island or across two islands. Both stay in the kitchen.\n\n' +
				'This is a positional swap. To put a DIFFERENT appliance in the same spot use replace_appliance instead.',
			source: 'blueprint',
			meta: {
				isAsync: false,
				modifiesBlueprint: true,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					applianceId: { type: 'string', description: 'The first appliance. Use list_appliances to get valid IDs.' },
					otherApplianceId: { type: 'string', description: 'The appliance it trades places with.' },
					autoSize: { type: 'boolean', description: 'Allow a receiving island to grow when the widths differ and it has no room (default true).' }
				},
				required: ['applianceId', 'otherApplianceId']
			}
		},
		{
			name: 'list_appliances',
			description:
				'[CLIENT ASYNC] Lists all appliances/items in the kitchen with their IDs and details.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: list_appliances] in history.\n\n' +
				'By default, system items (vents, passthroughs) are excluded. Pass includeSystem: true to include them.\n\n' +
				'Call with {} to list ALL appliances. Only pass islandId/category if user EXPLICITLY requests filtering.\n\n' +
				'Examples:\n' +
				'- "What appliances are in the kitchen?" -> call with {}\n' +
				'- "List all grills" -> call with {category: "grill"}',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					islandId: { type: 'string', description: 'ONLY pass if user explicitly asks to filter by a specific island.' },
					category: { type: 'string', description: 'ONLY pass if user explicitly asks to filter by category.' },
					includeSystem: { type: 'boolean', description: 'When true, includes system items (vents, passthroughs) in results. Defaults to false.' }
				}
			}
		},

		// Query tools
		{
			name: 'get_current_design',
			description:
				'[CLIENT ASYNC] Gets summary information about the currently loaded kitchen design.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_current_design] in history.\n\n' +
				'Returns:\n' +
				'- layout: Kitchen layout type (e.g., L-shaped, U-shaped, galley)\n' +
				'- description: Human-readable name/description of the kitchen\n' +
				'- islandCount: Number of islands\n' +
				'- islands: Array of island details:\n' +
				'  - id, type, width, depth (in inches)\n' +
				'  - groups: Appliance groups sorted left-to-right, each containing:\n' +
				'    - groupId: Group identifier for move_appliance_group\n' +
				'    - appliances: Items sorted top-to-bottom with id, sku, category, name\n' +
				'- materials: { countertop, surround } display names',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools',
				responseFields: ['layout', 'description', 'islandCount', 'islands', 'materials']
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'get_layout_options',
			description:
				'[CLIENT ASYNC] Gets available kitchen layout/shape options from the blueprint library.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_layout_options] in history.\n\n' +
				'Use to get available island configurations and layout shapes.',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					groupKey: {
						type: 'string',
						description: 'Optional group key to filter layouts.',
						enum: ['l-shaped', 'u-shaped', 'galley', 'grill', 'bar', 'grill-bar']
					}
				}
			}
		},
		{
			name: 'get_countertop_material_options',
			description:
				'[CLIENT ASYNC] Get available countertop material options.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_countertop_material_options] in history.\n\n' +
				'Material Types: "granite", "concrete", "all"\n' +
				'Color Tones (granite): "light", "dark", "all"\n' +
				'Edge Styles (concrete): "chiseled", "clean", "both"',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'get_surround_material_options',
			description:
				'[CLIENT ASYNC] Get available surround material options.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_surround_material_options] in history.\n\n' +
				'Material Types: "stone", "modernStone", "concrete", "brick", "wood", "all"',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'get_appliance_categories',
			description:
				'[CLIENT ASYNC] Gets the list of available appliance categories.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_appliance_categories] in history.\n\n' +
				'Returns categories with key (category ID) and displayName. Use the key value when calling get_appliances.',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {}
			}
		},
		{
			name: 'get_appliances',
			description:
				'[CLIENT ASYNC] Lists available appliances, optionally filtered by category.\n\n' +
				'ASYNC: Returns clientAction immediately. Data arrives via [TOOL_RESULT: get_appliances] in history.\n\n' +
				'Use get_appliance_categories to discover valid category keys. Pass a category key to filter results.\n' +
				'Use BEFORE add_appliance or replace_appliance to get valid SKUs.',
			source: 'blueprint',
			meta: {
				isAsync: true,
				modifiesBlueprint: false,
				debugCategory: 'client-tools'
			},
			parameters: {
				type: 'object',
				properties: {
					category: { type: 'string', description: 'Optional category key to filter by. Use get_appliance_categories to get valid keys.' }
				}
			}
		}
	];
};
